import MainLayout from 'Components/Layout/MainLayout';
import {Button, message, Space, Spin, Tag} from 'antd';
import React, {useRef} from 'react';
import Style from './index.module.scss';
import TableRender, {ProColumnsType, TableContext} from 'table-render';
import useTable from '../../../Utils/Hooks/useTable';
import {
    deleteBatch,
    deleteTag,
    deleteTagType,
    exportTag,
    GetTagList,
    getTagOptions,
    getTagPage,
    TagListProps,
} from './service';
import DeleteConfirm from '../../../Components/DeleteConfirm';
import useModalHandler from '../../../Utils/Hooks/useModalHandler';
import TagTypeImportModal from './Components/TagTypeImportModal';
import TagImportModal from './Components/TagImportModal';
import {useMutation} from 'react-query';
import OverText from '../../../Components/OverText';
import useRequest from '../../../Utils/Hooks/useRequest';
import EditModal, {TagForm} from './Components/EditModal';
import useSelection from '../../../Utils/Hooks/useSelection';
import {isEmpty} from '../../../Stores/commonStore';
import {useDict} from './dict';

export default function Index() {
    const rowSelection = useSelection();
    const {tableInit} = useTable(getTagPage, {pageSize: 20});
    const tableRef = useRef<TableContext>(null);
    const importTagModal = useModalHandler({
        component: TagImportModal,
    });
    const importTagTypeModal = useModalHandler({
        component: TagTypeImportModal,
    });
    const editModal = useModalHandler({
        component: EditModal,
    });
    const [options, , reloadOptions] = useRequest(getTagOptions);

    function reload() {
        tableRef?.current?.refresh();
        reloadOptions();
    }

    const onExport = useMutation({
        mutationFn: exportTag,
        onSuccess: () => {
            message.success('导出成功!');
        },
    });

    const onDeleteTagType = useMutation({
        mutationFn: deleteTagType,
        onSuccess: () => {
            reload();
        },
    });
    const onDeleteTag = useMutation({
        mutationFn: deleteTag,
        onSuccess: () => {
            reload();
        },
    });
    const onDeleteMany = useMutation({
        mutationFn: deleteBatch,
        onSuccess: () => {
            reload();
        },
    });

    function onDelete(id: string[], item?: TagListProps) {
        if (item?.content) {
            onDeleteTag.mutate(id);
        } else {
            onDeleteTagType.mutate(id);
        }
    }

    function onExportTag() {
        onExport.mutate(tableRef?.current?.getState().search as GetTagList);
    }

    function deleteMany() {
        let typeIds: string[] = [];
        let tagIds: string[] = [];
        rowSelection.selected.forEach(item => {
            const [id, type] = item.toString().split('-') as [string, TagForm];
            if (type === TagForm.Tag) {
                tagIds.push(id);
            }
            if (type === TagForm.Type) {
                typeIds.push(id);
            }
        });
        onDeleteMany.mutate({tagIds, typeIds});
    }

    const dict = useDict();

    const toolBar = (
        <div className={Style.toolBar}>
            <Space>
                <DeleteConfirm onSuccess={deleteMany}>
                    <Button
                        danger
                        ghost
                        disabled={isEmpty(rowSelection.selected)}>
                        批量删除
                    </Button>
                </DeleteConfirm>
                <Button
                    type="primary"
                    ghost
                    onClick={() =>
                        importTagTypeModal.open({
                            onOk: importTagTypeModal.close,
                        })
                    }>
                    导入一级分类
                </Button>
                <Button
                    type="primary"
                    ghost
                    onClick={() =>
                        importTagModal.open({onOk: importTagModal.close})
                    }>
                    导入二级分类
                </Button>
                <Button
                    type="primary"
                    ghost
                    onClick={onExportTag}
                    loading={onExport.isLoading}>
                    Excel导出
                </Button>
                <Button
                    type="primary"
                    onClick={() =>
                        editModal.open({
                            onOk: () => {
                                reload();
                                editModal.close();
                            },
                        })
                    }
                    loading={onExport.isLoading}>
                    新增
                </Button>
            </Space>
        </div>
    );

    const schema = {
        type: 'object',
        labelWidth: 100,
        properties: {
            content: {
                title: '名称',
                type: 'string',
                default: ' ',
            },
            typeId: {
                title: '一级分类',
                type: 'string',
                widget: 'select',
                default: ' ',
                props: {
                    allowClear: true,
                    options,
                },
            },
        },
    };

    const columns: ProColumnsType<TagListProps> = [
        {title: '', width: 80},
        {
            title: '名称',
            width: 160,
            render: (value, record) => {
                return (
                    <div>
                        {record?.color && (
                            <Tag color={record?.color} className={Style.color}>
                                &nbsp;
                            </Tag>
                        )}
                        {record?.content || record?.typeName}
                    </div>
                );
            },
        },
        {
            title: '类型',
            width: 220,
            dataIndex: 'catalog',
            render: dict?.TagType?.getName,
        },
        {
            title: '描述',
            dataIndex: 'description',
            render: value => <OverText text={value} length={100} />,
        },
        {
            title: '操作',
            fixed: 'right',
            dataIndex: 'id',
            align: 'center',
            width: 160,
            render: (id: string, item) => (
                <Space>
                    {!item?.content && (
                        <Button
                            size="small"
                            type="link"
                            onClick={() =>
                                editModal.open({
                                    id,
                                    isTag: true,
                                    defaultData: {
                                        ...item,
                                        typeId: item.id,
                                    },
                                    onOk: () => {
                                        reload();
                                        editModal.close();
                                    },
                                })
                            }>
                            添加下级
                        </Button>
                    )}
                    <Button
                        size="small"
                        type="link"
                        onClick={() =>
                            editModal.open({
                                id,
                                defaultData: item,
                                onOk: () => {
                                    reload();
                                    editModal.close();
                                },
                            })
                        }>
                        修改
                    </Button>
                    <DeleteConfirm onSuccess={() => onDelete([id], item)}>
                        <Button size="small" type="link">
                            删除
                        </Button>
                    </DeleteConfirm>
                </Space>
            ),
        },
    ];

    return (
        <MainLayout.SpaceWrapper padding>
            <Spin spinning={onDeleteTag?.isLoading || onDeleteTag?.isLoading}>
                <TableRender
                    rowKey="key"
                    ref={tableRef}
                    search={{schema, column: 4}}
                    columns={columns}
                    toolbarRender={toolBar}
                    scroll={{x: 'max-content'}}
                    rowSelection={{...rowSelection, checkStrictly: false}}
                    {...tableInit()}
                />
            </Spin>

            {importTagModal.render()}
            {importTagTypeModal.render()}
            {editModal.render()}
        </MainLayout.SpaceWrapper>
    );
}
